import { Badge, Box, Group, Text, ThemeIcon, Timeline, Tooltip, UnstyledButton } from '@mantine/core';
import { createElement, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  groupCountLabel,
  groupTitle,
  groupWhere,
  memberLabel,
  type EventGroupItem,
  type EventListItem,
} from './groupEvents';
import {
  SEVERITY_COLOR, clockTime, eventIcon, formatFullDateTime, formatOffset, formatSpan, type TimelineEventView,
} from './timelineEvent';

const relative = (time: string | Date) => formatDistanceToNow(new Date(time), { addSuffix: true });

/** The body of one activity row — used for single events and for group
 *  members. A single shows the relative time with a full-timestamp tooltip;
 *  a group member (`groupStart` set) shows its absolute clock time with
 *  seconds plus its offset from the group's first event, since every member
 *  of a tight burst otherwise reads the same "2 minutes ago". `label`
 *  replaces the title (one alarm zone's member: its floor), and the location
 *  line then goes. */
function ActivityRowContent({ event, groupStart, label }: {
  event: TimelineEventView;
  groupStart?: Date;
  label?: string | null;
}) {
  return (
    <>
      <Group gap={8} wrap="nowrap" align="baseline" justify="space-between">
        <Text size="sm" fw={500} truncate flex={1} miw={0} title={event.title}>{label || event.title}</Text>
        {groupStart ? (
          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
              {clockTime(event.time)}
            </Text>
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {formatOffset(event.time, groupStart)}
            </Text>
          </Group>
        ) : (
          <Tooltip label={formatFullDateTime(event.time)} withArrow>
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              {relative(event.time)}
            </Text>
          </Tooltip>
        )}
      </Group>
      {event.description && (
        <Text size="xs" c="dimmed" lineClamp={2}>{event.description}</Text>
      )}
      <Group gap={12} wrap="wrap" mt={2}>
        {event.actorName && <Text size="xs" c="dimmed">{event.actorName}</Text>}
        {event.deviceName && <Text size="xs" c="dimmed">{event.deviceName}</Text>}
        {event.location && !label && <Text size="xs" c="dimmed">{event.location}</Text>}
      </Group>
    </>
  );
}

function EventBullet({ event, size }: { event: TimelineEventView; size: number }) {
  return (
    <ThemeIcon size={size} variant="light" radius="xl" color={SEVERITY_COLOR[event.severity] ?? 'blue'}>
      {createElement(eventIcon(event.kind), { size: size / 2 })}
    </ThemeIcon>
  );
}

function ActivityGroupHeader({ group, expanded, onToggle }: {
  group: EventGroupItem<TimelineEventView>;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { events } = group;
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const where = groupWhere(group);
  return (
    <UnstyledButton
      onClick={onToggle}
      w="100%"
      aria-expanded={expanded}
      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${events.length} related events`}
    >
      <Group gap={8} wrap="nowrap" align="baseline" justify="space-between">
        <Text size="sm" fw={500} lineClamp={2} flex={1} miw={0}>{groupTitle(group)}</Text>
        <Text
          size="xs"
          c="dimmed"
          style={{ whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'monospace' }}
        >
          {formatSpan(group.start, group.end)}
        </Text>
      </Group>
      <Group gap={12} wrap="nowrap" mt={2} justify="space-between" align="flex-start">
        <Group gap={12} wrap="wrap" miw={0}>
          {group.actors.length > 0 && <Text size="xs" c="dimmed">{group.actors.join(', ')}</Text>}
          {where && <Text size="xs" c="dimmed">{where}</Text>}
        </Group>
        <Badge
          size="sm"
          variant="light"
          color="gray"
          tt="none"
          rightSection={<Chevron size={12} />}
          style={{ flexShrink: 0, cursor: 'pointer' }}
        >
          {groupCountLabel(group)}
        </Badge>
      </Group>
    </UnstyledButton>
  );
}

export interface ActivityTimelineProps<V extends TimelineEventView = TimelineEventView> {
  /** Output of `groupEvents()`, already trimmed to the rows to show. */
  items: EventListItem<V>[];
}

/** The compact "Recent activity" feed: a Mantine Timeline with a severity-
 *  coloured icon bullet per row. A single event shows its relative time
 *  (tooltip: full timestamp). A burst of related events is one row
 *  ("N events") with its time span (seconds), expanding into a nested
 *  timeline of members — each shown by absolute clock time + offset from the
 *  burst's start, so the order within it reads at a glance. Groups start
 *  COLLAPSED. Loading / error / empty states are the caller's. */
export function ActivityTimeline<V extends TimelineEventView = TimelineEventView>({
  items,
}: ActivityTimelineProps<V>) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Timeline bulletSize={28} lineWidth={2} color="gray">
      {items.map((item) => {
        if (item.kind === 'single') {
          return (
            <Timeline.Item key={item.key} bullet={<EventBullet event={item.event} size={28} />}>
              <ActivityRowContent event={item.event} />
            </Timeline.Item>
          );
        }
        const isOpen = expanded.has(item.key);
        return (
          <Timeline.Item
            key={item.key}
            bullet={<EventBullet event={item.headline} size={28} />}
            data-event-group={item.key}
          >
            <ActivityGroupHeader group={item} expanded={isOpen} onToggle={() => toggle(item.key)} />
            {isOpen && (
              <Box mt="xs" pl={4}>
                <Timeline bulletSize={20} lineWidth={1} color="gray" styles={{ item: { marginTop: 'var(--mantine-spacing-sm)' } }}>
                  {item.events.map((event) => (
                    <Timeline.Item key={event.id} bullet={<EventBullet event={event} size={20} />}>
                      <ActivityRowContent event={event} groupStart={item.start} label={memberLabel(item, event)} />
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Box>
            )}
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}
